import React, { useState, useEffect } from 'react';
import { signInWithPopup, signOut } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { ref as dbRef, onValue, off, set, remove, get, update, query, orderByChild, endAt } from 'firebase/database';
import { auth, provider, storage, database } from '../../firebase';
import logo from '../../images/coroapp-logo.png';
import { v4 as uuidv4 } from 'uuid';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { PDFDocument } from 'pdf-lib';

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const AdminPanel = () => {
  const [user, setUser] = useState(null);
  const [songs, setSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newSong, setNewSong] = useState({ name: '', filename: '', themes: [''] });
  const [file, setFile] = useState(null);
  const [compressedFile, setCompressedFile] = useState(null);
  const [originalSize, setOriginalSize] = useState(null);
  const [compressedSize, setCompressedSize] = useState(null);
  const [editingSong, setEditingSong] = useState(null);
  const [selectedSong, setSelectedSong] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isProductionModalOpen, setIsProductionModalOpen] = useState(false);
  const [songToDelete, setSongToDelete] = useState(null);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [availableThemes, setAvailableThemes] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [productionVersion, setProductionVersion] = useState(0);
  const [changes, setChanges] = useState({ added: [], updated: [], deleted: [] });
  const [updateHistoryByVersion, setUpdateHistoryByVersion] = useState({});
  const [expandedVersions, setExpandedVersions] = useState({});
  const [globalUpdateCounter, setGlobalUpdateCounter] = useState(0);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);
  const [isTestModeModalOpen, setIsTestModeModalOpen] = useState(false);
  const [testModeAction, setTestModeAction] = useState(''); // 'activate' or 'deactivate'
  const [realCurrentVersion, setRealCurrentVersion] = useState(0);
  const [realProductionVersion, setRealProductionVersion] = useState(0);
  const [testModePassword, setTestModePassword] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [metadataLoaded, setMetadataLoaded] = useState(false);

  const generateFilename = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_');
  };

  useEffect(() => {
    const themes = new Set();
    songs.forEach((song) => {
      if (Array.isArray(song.themes) && song.themes.length > 0) {
        song.themes.forEach((theme) => themes.add(theme));
      }
    });
    setAvailableThemes([...themes]);
  }, [songs]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        let isAdmin = false;
        try {
          const adminRef = dbRef(database, `coroapp/admins/${currentUser.uid}`);
          const snapshot = await get(adminRef);
          if (snapshot.exists() && snapshot.val() === currentUser.email) {
            isAdmin = true;
          }
        } catch (error) {
          console.error("Could not check admin status, assuming non-admin.", error);
          isAdmin = false;
        }
        if (isAdmin) {
          setUser(currentUser);
          await syncDatabaseWithStorage();
          fetchSongs();
          fetchMetadata();
        } else {
          await signOut(auth);
          setUser(null);
          setError('Solo administradores. No puedes acceder.');
        }
      } else {
        setUser(null);
      }
    });
    return () => {
      unsubscribe();
      const songsRef = dbRef(database, 'coroapp/cantos');
      const metadataRef = dbRef(database, 'coroapp/metadata');
      off(songsRef);
      off(metadataRef);
    };
  }, []);

  useEffect(() => {
    if (selectedSong && selectedSong.filename) {
      const fetchPreviewUrl = async () => {
        try {
          setLoading(true);
          const storageRef = ref(storage, `coroapp/pdfs/${selectedSong.filename}.pdf`);
          const url = await getDownloadURL(storageRef);
          setPreviewUrl(url);
          setError('');
        } catch (err) {
          setError('Error al cargar la vista previa del PDF: ' + err.message);
          setPreviewUrl('');
        } finally {
          setLoading(false);
        }
      };
      fetchPreviewUrl();
    } else {
      setPreviewUrl('');
    }
  }, [selectedSong]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredSongs(songs);
    } else {
      const normalizeText = (text) => text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const normalizedQuery = normalizeText(searchQuery);
      setFilteredSongs(
        songs.filter((song) => {
          const normalizedName = normalizeText(song.name);
          const normalizedThemes = Array.isArray(song.themes)
            ? song.themes.map((theme) => normalizeText(theme))
            : [];
          return (
            normalizedName.includes(normalizedQuery) ||
            normalizedThemes.some((theme) => theme.includes(normalizedQuery))
          );
        })
      );
    }
  }, [searchQuery, songs]);

  const confirmDownloadAll = async () => {
    setIsDownloadModalOpen(false);
    setLoading(true);
    setError('');
    try {
      const zip = new JSZip();
      const storageRef = ref(storage, 'coroapp/pdfs');
      const fileList = await listAll(storageRef);
      const fetchPromises = fileList.items.map(async (item) => {
        const url = await getDownloadURL(item);
        const response = await fetch(url);
        const blob = await response.blob();
        zip.file(item.name, blob);
      });
      await Promise.all(fetchPromises);
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, 'partituras_coroapp.zip');
    } catch (err) {
      setError('Error al descargar todas las partituras: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelDownload = () => {
    setIsDownloadModalOpen(false);
  };

  const syncDatabaseWithStorage = async () => {
    try {
      const storageRef = ref(storage, 'coroapp/pdfs');
      const fileList = await listAll(storageRef);
      const songsRef = dbRef(database, 'coroapp/cantos');
      const snapshot = await get(songsRef);
      const existingSongs = snapshot.val() || {};
      for (const file of fileList.items) {
        const filename = file.name.replace('.pdf', '');
        const songExists = Object.keys(existingSongs).some(
          (id) => existingSongs[id].filename === filename
        );
        if (!songExists) {
          const songData = {
            name: filename
              .replace(/_/g, ' ')
              .split(' ')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' '),
            filename,
            themes: ['Alabanza'],
          };
          const updateId = uuidv4();
          await set(dbRef(database, `coroapp/cantos/${filename}`), songData);
          await set(dbRef(database, `coroapp/metadata/historical/${updateId}`), {
            type: 'added',
            filename,
            name: songData.name,
            themes: songData.themes,
            user: user.displayName || user.email || 'Usuario Desconocido',
            timestamp: new Date().toISOString(),
            updateNumber: globalUpdateCounter + 1,
            version: currentVersion,
          });
          setGlobalUpdateCounter((prev) => prev + 1);
          setChanges((prev) => ({
            ...prev,
            added: [...(prev.added || []).filter((f) => f !== filename), filename],
          }));
          await update(dbRef(database, `coroapp/metadata/versions/${currentVersion}/changes`), {
            added: [...(changes.added || []).filter((f) => f !== filename), filename],
          });
        }
      }
    } catch (err) {
      setError('Error al sincronizar almacenamiento con base de datos: ' + err.message);
    }
  };

  const fetchSongs = () => {
    const songsRef = dbRef(database, 'coroapp/cantos');
    onValue(
      songsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const songsArray = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
            themes: Array.isArray(data[key].themes) ? data[key].themes : [],
          }));
          setSongs(songsArray);
        } else {
          setSongs([]);
        }
      },
      (err) => {
        setError('Error al obtener partituras: ' + err.message);
      }
    );
  };

  const fetchMetadata = () => {
    const metadataRef = dbRef(database, 'coroapp/metadata');
    onValue(
      metadataRef,
      (snapshot) => {
        const data = snapshot.val();
        console.log('Metadata snapshot:', data); // Debug log
        if (data) {
          setCurrentVersion(data.currentVersion ?? 1);
          setProductionVersion(data.productionVersion ?? 0);
          const versionChanges = data.versions?.[data.currentVersion]?.changes || { added: [], updated: [], deleted: [] };
          setChanges({
            added: versionChanges.added || [],
            updated: versionChanges.updated || [],
            deleted: versionChanges.deleted || [],
          });
          const historical = data.historical || {};
          const history = Object.entries(historical)
            .map(([id, update]) => ({
              id,
              ...update,
            }))
            // Include updates up to currentVersion (not just productionVersion)
            .filter((update) => update.version <= data.currentVersion);
          const historyByVersion = history.reduce((acc, update) => {
            const version = update.version || 'unknown';
            if (!acc[version]) {
              acc[version] = [];
            }
            acc[version].push(update);
            return acc;
          }, {});
          const sortedHistoryByVersion = Object.keys(historyByVersion)
            .sort((a, b) => Number(b) - Number(a))
            .reduce((acc, version) => {
              acc[version] = historyByVersion[version].sort(
                (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
              );
              return acc;
            }, {});
          console.log('Processed updateHistoryByVersion:', sortedHistoryByVersion); // Debug log
          setUpdateHistoryByVersion(sortedHistoryByVersion);
          const maxUpdateNumber = history.length > 0 ? Math.max(...history.map((u) => u.updateNumber || 0)) : 0;
          setGlobalUpdateCounter(maxUpdateNumber);
          setExpandedVersions(
            Object.keys(sortedHistoryByVersion).reduce((acc, version) => {
              acc[version] = version === String(data.productionVersion);
              return acc;
            }, {})
          );
          setMaintenanceMode(data.maintenanceMode ?? false);
          setMetadataLoaded(true);
        } else {
          const initialMetadata = {
            currentVersion: 1,
            productionVersion: 0,
            versions: {
              1: { changes: { added: [], updated: [], deleted: [] } },
            },
            historical: {},
            maintenanceMode: false,
          };
          set(dbRef(database, 'coroapp/metadata'), initialMetadata);
          setChanges({ added: [], updated: [], deleted: [] });
          setUpdateHistoryByVersion({});
          setExpandedVersions({});
          setMaintenanceMode(false);
          setMetadataLoaded(true);
        }
      },
      (err) => {
        setError('Error al obtener metadatos: ' + err.message);
        setMetadataLoaded(true);
      }
    );
  };

  const toggleVersion = (version) => {
    setExpandedVersions((prev) => ({
      ...prev,
      [version]: !prev[version],
    }));
  };

  const activateTestMode = async () => {
    setLoading(true);
    try {
      // Store the real versions
      setRealCurrentVersion(currentVersion);
      setRealProductionVersion(productionVersion);
      // Set test mode versions
      setCurrentVersion(-100);
      setProductionVersion(-101);
      // Clear any previous test mode data from Firebase
      const testModeDataRef = dbRef(database, 'coroapp/testModeData');
      await remove(testModeDataRef);
      // Update versions in the database
      const metadataRef = dbRef(database, 'coroapp/metadata');
      await update(metadataRef, {
        currentVersion: -100,
        productionVersion: -101,
      });
      setIsTestMode(true);
      setError('');
    } catch (err) {
      setError('Error al activar el modo prueba: ' + err.message);
      // Rollback UI changes if activation fails
      setIsTestMode(false);
      setCurrentVersion(realCurrentVersion);
      setProductionVersion(realProductionVersion);
    } finally {
      setLoading(false);
      setIsTestModeModalOpen(false);
    }
  };

  const deactivateTestMode = async () => {
    setLoading(true);
    setError('');
    try {
      const logRef = dbRef(database, 'coroapp/testModeData/log');
      const logSnapshot = await get(logRef);
      if (logSnapshot.exists()) {
        const logs = logSnapshot.val();
        const promises = Object.keys(logs).map(async (logId) => {
          const log = logs[logId];
          const songId = log.id;
          const filename = log.filename;
          if (log.type === 'added') {
            // Delete the added song
            const songRef = dbRef(database, `coroapp/cantos/${songId}`);
            await remove(songRef);
            const storageRef = ref(storage, `coroapp/pdfs/${filename}.pdf`);
            await deleteObject(storageRef).catch((err) => {
              if (err.code !== 'storage/object-not-found') throw err;
            });
          } else if (log.type === 'updated' || log.type === 'deleted') {
            // Restore the backed-up song from DB
            const backupDbRef = dbRef(database, `coroapp/testModeData/backups/db/${songId}`);
            const backupSnapshot = await get(backupDbRef);
            if (backupSnapshot.exists()) {
              const songData = backupSnapshot.val();
              const songRef = dbRef(database, `coroapp/cantos/${songId}`);
              await set(songRef, songData);
            } else {
              // If there's no DB backup, it means this was a newly added item that was then modified/deleted.
              // In this scenario, the correct rollback is to simply delete it.
              const songRef = dbRef(database, `coroapp/cantos/${songId}`);
              await remove(songRef);
            }
            // Restore the backed-up file from Storage
            const backupStorageRef = ref(storage, `coroapp/testModeData/backups/storage/${filename}.pdf`);
            const originalStorageRef = ref(storage, `coroapp/pdfs/${filename}.pdf`);
            try {
              const url = await getDownloadURL(backupStorageRef);
              const response = await fetch(url);
              const blob = await response.blob();
              await uploadBytes(originalStorageRef, blob);
            } catch (err) {
               if (err.code === 'storage/object-not-found') {
                // If backup file doesn't exist, it might mean the original song never had a PDF.
                // We should ensure the original path is also clear.
                await deleteObject(originalStorageRef).catch(e => {
                  if (e.code !== 'storage/object-not-found') throw e;
                });
               } else {
                 throw err;
               }
            }
          }
        });
        await Promise.all(promises);
      }
      // Delete historical records created in test mode
      const historicalRef = dbRef(database, 'coroapp/metadata/historical');
      const testHistoryQuery = query(historicalRef, orderByChild('version'), endAt(-1));
      const historySnapshot = await get(testHistoryQuery);
      if (historySnapshot.exists()) {
        const historyDeletions = [];
        historySnapshot.forEach((childSnapshot) => {
          historyDeletions.push(remove(childSnapshot.ref));
        });
        await Promise.all(historyDeletions);
      }
      // Delete version records from test mode
      const versionsRef = dbRef(database, 'coroapp/metadata/versions');
      const versionsSnapshot = await get(versionsRef);
      if (versionsSnapshot.exists()) {
        const versionsData = versionsSnapshot.val();
        const versionDeletions = [];
        for (const versionKey in versionsData) {
          if (Number(versionKey) < 0) {
            const versionToDeleteRef = dbRef(database, `coroapp/metadata/versions/${versionKey}`);
            versionDeletions.push(remove(versionToDeleteRef));
          }
        }
        await Promise.all(versionDeletions);
      }
      // Clean up test mode data from Firebase
      const testModeDataRef = dbRef(database, 'coroapp/testModeData');
      await remove(testModeDataRef);
      // Revert versions in the database
      const metadataRef = dbRef(database, 'coroapp/metadata');
      await update(metadataRef, {
        currentVersion: realCurrentVersion,
        productionVersion: realProductionVersion,
      });
      // Revert to real versions and state
      setCurrentVersion(realCurrentVersion);
      setProductionVersion(realProductionVersion);
      setIsTestMode(false);
      // Refresh data
      await fetchSongs();
      await fetchMetadata();
    } catch (err) {
      setError('Error al desactivar el modo prueba: ' + err.message);
      // Attempt to restore a sane state
      setCurrentVersion(realCurrentVersion);
      setProductionVersion(realProductionVersion);
      setIsTestMode(false);
    } finally {
      setLoading(false);
      setIsTestModeModalOpen(false);
    }
  };

  const handleToggleTestMode = async () => {
    if (testModeAction === 'activate') {
      if (testModePassword !== process.env.REACT_APP_ADMINPANEL_TESTMODE_PASSWORD) {
        setError('');
        setModalError('Contraseña incorrecta. No se puede activar el modo prueba.');
        setTestModePassword('');
        return;
      }
      await activateTestMode();
      setModalError('');
      setTestModePassword(''); // Limpiar el campo de contraseña tras activación exitosa
    } else if (testModeAction === 'deactivate') {
      await deactivateTestMode();
    }
  };

  const backupSongForTestMode = async (song, type) => {
    // Check if this song has already been backed up in this session
    const logRef = dbRef(database, `coroapp/testModeData/log/${song.id}`);
    const logSnapshot = await get(logRef);
    if (logSnapshot.exists()) {
      console.log(`Song ${song.id} already has a backup in this session. Skipping.`);
      return;
    }
    // Backup DB record
    const originalSongRef = dbRef(database, `coroapp/cantos/${song.id}`);
    const snapshot = await get(originalSongRef);
    if (snapshot.exists()) {
      const originalSongData = snapshot.val();
      const backupDbRef = dbRef(database, `coroapp/testModeData/backups/db/${song.id}`);
      await set(backupDbRef, originalSongData);
    }
    // Backup Storage file
    const storageFileRef = ref(storage, `coroapp/pdfs/${song.filename}.pdf`);
    const backupStorageFileRef = ref(storage, `coroapp/testModeData/backups/storage/${song.filename}.pdf`);
    try {
      const url = await getDownloadURL(storageFileRef);
      const response = await fetch(url);
      const blob = await response.blob();
      await uploadBytes(backupStorageFileRef, blob);
    } catch (error) {
      if (error.code === 'storage/object-not-found') {
        console.warn(`PDF for ${song.filename} not found, skipping backup.`);
      } else {
        throw error;
      }
    }
    // Log the operation
    const newLogRef = dbRef(database, `coroapp/testModeData/log/${song.id}`);
    await set(newLogRef, { type, id: song.id, filename: song.filename });
  };

  const logAdditionInTestMode = async (song) => {
    const logRef = dbRef(database, `coroapp/testModeData/log/${song.filename}`);
    await set(logRef, { type: 'added', id: song.filename, filename: song.filename });
  };

  const handleSendToProduction = async () => {
    try {
      setLoading(true);
      setError('');
      const newProductionVersion = currentVersion;
      const newCurrentVersion = currentVersion + 1;
      await update(dbRef(database, 'coroapp/metadata'), {
        productionVersion: newProductionVersion,
        currentVersion: newCurrentVersion,
        [`versions/${newCurrentVersion}/changes`]: { added: [], updated: [], deleted: [] },
      });
      setProductionVersion(newProductionVersion);
      setCurrentVersion(newCurrentVersion);
      setChanges({ added: [], updated: [], deleted: [] });
      setExpandedVersions((prev) => ({
        ...prev,
        [newProductionVersion]: true,
      }));
      fetchMetadata();
      setLoading(false);
      setIsProductionModalOpen(false);
    } catch (err) {
      setError('Error al enviar a producción: ' + err.message);
      setLoading(false);
      setIsProductionModalOpen(false);
    }
  };

  const handleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      let isAdmin = false;
      try {
        const adminRef = dbRef(database, `coroapp/admins/${user.uid}`);
        const snapshot = await get(adminRef);
        if (snapshot.exists() && snapshot.val() === user.email) {
          isAdmin = true;
        }
      } catch (e) {
        console.error("Could not check admin status, assuming non-admin.", e);
        isAdmin = false;
      }
      if (isAdmin) {
        setUser(user);
        setError('');
      } else {
        await signOut(auth);
        setUser(null);
        setError('Solo administradores. No puedes acceder.');
      }
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError('Error al iniciar sesión: ' + err.message);
      }
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setSongs([]);
      setFilteredSongs([]);
      setSelectedSong(null);
      setPreviewUrl('');
      setUpdateHistoryByVersion({});
      setExpandedVersions({});
      setMetadataLoaded(false);
      const songsRef = dbRef(database, 'coroapp/cantos');
      const metadataRef = dbRef(database, 'coroapp/metadata');
      off(songsRef);
      off(metadataRef);
    } catch (err) {
      setError('Error al cerrar sesión: ' + err.message);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleInputChange = (e, index = null) => {
    const { name, value } = e.target;
    if (name === 'themes') {
      const updatedThemes = [...newSong.themes];
      updatedThemes[index] = value;
      setNewSong({ ...newSong, themes: updatedThemes });
    } else if (name === 'name') {
      setNewSong({
        ...newSong,
        name: value,
        filename: editingSong ? newSong.filename : generateFilename(value),
      });
    }
  };

  const addTheme = () => {
    setNewSong({ ...newSong, themes: [...newSong.themes, ''] });
  };

  const removeTheme = (index) => {
    const updatedThemes = newSong.themes.filter((_, i) => i !== index);
    setNewSong({ ...newSong, themes: updatedThemes });
  };

  const validateForm = () => {
    if (!newSong.name.trim()) {
      setModalError('El nombre de la partitura es obligatorio.');
      return false;
    }
    if (newSong.themes.every((theme) => !theme.trim())) {
      setModalError('Debe seleccionar al menos un tema.');
      return false;
    }
    if (!editingSong && !file) {
      setModalError('El archivo PDF es obligatorio al agregar una nueva partitura.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalError('');
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (isTestMode) {
        if (editingSong) {
          // This is an update, back up the original song first
          await backupSongForTestMode(editingSong, 'updated');
        }
      }
      const newSongRef = dbRef(database, `coroapp/cantos/${newSong.filename}`);
      const snapshot = await get(newSongRef);
      if (!editingSong && snapshot.exists()) {
        setModalError('Ya existe una partitura con este nombre de archivo. Por favor, usa un nombre diferente.');
        setLoading(false);
        return;
      }
      if (editingSong && newSong.filename !== editingSong.filename && snapshot.exists()) {
        setModalError('Ya existe una partitura con el nuevo nombre de archivo. Por favor, usa un nombre diferente.');
        setLoading(false);
        return;
      }
      let fileToUpload = file;
      if (file) {
        try {
          const originalBytes = await file.arrayBuffer();
          setOriginalSize(originalBytes.byteLength);
          const pdfDoc = await PDFDocument.load(originalBytes);
          const compressedBytes = await pdfDoc.save();
          setCompressedSize(compressedBytes.byteLength);
         
          const compressedBlob = new Blob([compressedBytes], { type: 'application/pdf' });
          fileToUpload = new File([compressedBlob], file.name, {
            type: 'application/pdf',
            lastModified: Date.now(),
          });
        } catch (pdfError) {
          console.error("Error processing PDF:", pdfError);
          setModalError("No se pudo procesar el PDF. Asegúrate de que sea un archivo válido.");
          setLoading(false);
          return;
        }
        const storageRef = ref(storage, `coroapp/pdfs/${newSong.filename}.pdf`);
        await uploadBytes(storageRef, fileToUpload);
        if (editingSong && newSong.filename !== editingSong.filename) {
          const oldStorageRef = ref(storage, `coroapp/pdfs/${editingSong.filename}.pdf`);
          await deleteObject(oldStorageRef).catch((err) => {
            if (err.code !== 'storage/object-not-found') {
              throw err;
            }
          });
        }
      }
      const songData = {
        name: newSong.name,
        filename: newSong.filename,
        themes: newSong.themes.filter((theme) => theme.trim() !== ''),
      };
      const updateId = uuidv4();
      const updateData = {
        type: editingSong ? 'updated' : 'added',
        filename: newSong.filename,
        user: user.displayName || user.email || 'Usuario Desconocido',
        timestamp: new Date().toISOString(),
        updateNumber: globalUpdateCounter + 1,
        version: currentVersion,
      };
      if (editingSong) {
        updateData.name = newSong.name; // Añadido: siempre establecer el nombre actual para actualizaciones
        if (file) {
          updateData.pdfUpdated = true;
        }
        if (editingSong.name !== newSong.name) {
          updateData.oldName = editingSong.name;
          updateData.newName = newSong.name;
        }
        if (JSON.stringify(editingSong.themes) !== JSON.stringify(songData.themes)) {
          updateData.oldThemes = editingSong.themes;
          updateData.newThemes = songData.themes;
        }
        if (newSong.filename !== editingSong.filename) {
          const oldSongRef = dbRef(database, `coroapp/cantos/${editingSong.id}`);
          await remove(oldSongRef);
          const deleteUpdateId = uuidv4();
          await set(dbRef(database, `coroapp/metadata/historical/${deleteUpdateId}`), {
            type: 'deleted',
            filename: editingSong.filename,
            name: editingSong.name,
            themes: editingSong.themes,
            user: user.displayName || user.email || 'Usuario Desconocido',
            timestamp: new Date().toISOString(),
            updateNumber: globalUpdateCounter + 1,
            version: currentVersion,
          });
          setGlobalUpdateCounter((prev) => prev + 1);
          setChanges((prev) => ({
            ...prev,
            deleted: [...(prev.deleted || []).filter((f) => f !== editingSong.filename), editingSong.filename],
          }));
          await update(dbRef(database, `coroapp/metadata/versions/${currentVersion}/changes`), {
            deleted: [...(changes.deleted || []).filter((f) => f !== editingSong.filename), editingSong.filename],
          });
        }
      } else {
        updateData.name = newSong.name;
        updateData.themes = songData.themes;
      }
      await set(dbRef(database, `coroapp/cantos/${newSong.filename}`), songData);
      await set(dbRef(database, `coroapp/metadata/historical/${updateId}`), updateData);
      if (isTestMode && !editingSong) {
        // This was an addition, log it for rollback
        await logAdditionInTestMode(newSong);
      }
      setGlobalUpdateCounter((prev) => prev + 1);
      const changeType = editingSong ? 'updated' : 'added';
      const newChanges = { ...changes };
      if (changeType === 'updated') {
        const changeDetails = {};
        if (file) changeDetails.file = true;
        if (editingSong.name !== newSong.name) changeDetails.name = true;
        if (JSON.stringify(editingSong.themes) !== JSON.stringify(songData.themes)) changeDetails.themes = true;
        const updatedItem = {
          filename: newSong.filename,
          changes: changeDetails,
        };
        const updatedList = (changes.updated || [])
          .filter(item => (typeof item === 'string' ? item : item.filename) !== newSong.filename);
       
        newChanges.updated = [...updatedList, updatedItem];
      } else {
        const list = (changes[changeType] || []).filter(f => f !== newSong.filename);
        newChanges[changeType] = [...list, newSong.filename];
      }
      setChanges(newChanges);
      await update(dbRef(database, `coroapp/metadata/versions/${currentVersion}/changes`), {
        [changeType]: newChanges[changeType],
      });
      setNewSong({ name: '', filename: '', themes: [''] });
      setFile(null);
      setEditingSong(null);
      // Automatically open the preview for the newly added/edited song
      const newSelectedSong = { ...songData, id: newSong.filename };
      setSelectedSong(newSelectedSong);
      setIsPreviewModalOpen(true);
      setIsAddModalOpen(false);
      setModalError('');
      setLoading(false);
    } catch (err) {
      setError('Error al guardar la partitura: ' + err.message);
      setLoading(false);
    }
  };

  const handleEdit = (song) => {
    setEditingSong(song);
    setNewSong({
      name: song.name,
      filename: song.filename,
      themes: Array.isArray(song.themes) && song.themes.length > 0 ? song.themes : [''],
    });
    setModalError('');
    setIsAddModalOpen(true);
  };

  const handleDelete = (song) => {
    setSongToDelete(song);
    setDeleteConfirmationText('');
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!songToDelete) return;
    setLoading(true);
    setError('');
    try {
      if (isTestMode) {
        await backupSongForTestMode(songToDelete, 'deleted');
      }
      const songRef = dbRef(database, `coroapp/cantos/${songToDelete.id}`);
      await remove(songRef);
      const storageRef = ref(storage, `coroapp/pdfs/${songToDelete.filename}.pdf`);
      await deleteObject(storageRef).catch((err) => {
        if (err.code !== 'storage/object-not-found') {
          throw err;
        }
      });
      const updateId = uuidv4();
      await set(dbRef(database, `coroapp/metadata/historical/${updateId}`), {
        type: 'deleted',
        filename: songToDelete.filename,
        name: songToDelete.name,
        themes: songToDelete.themes,
        user: user.displayName || user.email || 'Usuario Desconocido',
        timestamp: new Date().toISOString(),
        updateNumber: globalUpdateCounter + 1,
        version: currentVersion,
      });
      setGlobalUpdateCounter((prev) => prev + 1);
      setChanges((prev) => ({
        ...prev,
        deleted: [...(prev.deleted || []).filter((f) => f !== songToDelete.filename), songToDelete.filename],
      }));
      await update(dbRef(database, `coroapp/metadata/versions/${currentVersion}/changes`), {
        deleted: [...(changes.deleted || []).filter((f) => f !== songToDelete.filename), songToDelete.filename],
      });
      setSelectedSong(null);
      setPreviewUrl('');
      setIsPreviewModalOpen(false);
      setIsDeleteModalOpen(false);
      setSongToDelete(null);
      setDeleteConfirmationText('');
      setLoading(false);
    } catch (err) {
      setError('Error al eliminar la partitura: ' + err.message);
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setSongToDelete(null);
    setDeleteConfirmationText('');
  };

  const handleSelectSong = (song) => {
    setSelectedSong(song);
    setIsPreviewModalOpen(true);
  };

  const handlePdfError = () => {
    setError('Error al cargar el PDF. Verifica que el archivo exista y sea accesible.');
    setPreviewUrl('');
  };

  const handleDownloadAll = () => {
    setIsDownloadModalOpen(true);
  };

  const hasPendingChanges = () => {
    return (
      (changes.added?.length || 0) > 0 ||
      (changes.updated?.length || 0) > 0 ||
      (changes.deleted?.length || 0) > 0
    );
  };

  if (!user) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="border border-gray-600 p-4 sm:p-12 flex flex-col items-center">
          <img src={logo} alt="CoroApp Logo" className="w-16 h-16 sm:w-24 sm:h-24 mb-4" />
          <p classModo="mt-4 text-gray-600 font-mono text-sm">PANEL DE ADMINISTRACIÓN</p>
          <p className="text-gray-600 font-mono text-sm">CORO LLDM</p>
          <br />
          {error && (
            <div className="mb-4 p-3 text-center text-sm text-red-700 bg-red-100 border border-red-400 rounded-lg font-mono">
              {error}
            </div>
          )}
          <button
            onClick={handleSignIn}
            className="px-8 py-3 border border-gray-600 text-gray-800 bg-white hover:bg-gray-600 hover:text-white transition-colors font-mono text-sm uppercase tracking-wider"
          >
            Iniciar Sesión con Google
          </button>
        </div>
      </div>
    );
  }

  if (!metadataLoaded) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="border border-gray-600 p-4 sm:p-12 flex flex-col items-center">
          <img src={logo} alt="CoroApp Logo" className="w-16 h-16 sm:w-24 sm:h-24 mb-4" />
          <p className="mt-4 text-gray-600 font-mono text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  if (maintenanceMode) {
  return (
    <div className="h-screen flex items-center justify-center bg-white">
      <div className="border border-gray-600 p-6 sm:p-12 flex flex-col items-center max-w-sm text-center">
        
        <img 
          src={logo} 
          alt="CoroApp Logo" 
          className="w-16 h-16 sm:w-24 sm:h-24 mb-6"
        />

        <p className="text-gray-800 font-mono text-base tracking-wide">
         ATENCIÓN
        </p>

        <p className="mt-2 text-gray-600 font-mono text-sm">
          El panel de administración se ha trasladado a un nuevo enlace.
        </p>

        {/* BOTÓN PRINCIPAL DESTACADO */}
        <button
          onClick={() => window.location.href = "https://lldm-coro.web.app"}
          className="mt-6 px-8 py-3 border border-gray-600 bg-gray-700 text-white font-mono text-sm uppercase tracking-wider transition-colors shadow-sm hover:bg-gray-900"
        >
          Ir al nuevo enlace
        </button>

        <button
          onClick={handleSignOut}
          className="mt-4 px-8 py-3 border border-gray-600 text-gray-800 bg-white hover:bg-gray-600 hover:text-white transition-colors font-mono text-sm uppercase tracking-wider"
        >
          Cerrar Sesión
        </button>
      </div>
    </div>
  );
}


  return (
    <div className="min-h-screen bg-white flex flex-col p-2 sm:p-8">
      <div className="flex-grow max-w-7xl mx-auto w-full flex flex-col">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-12 pb-4 border-b border-gray-600">
          <div className="flex items-center mb-4 sm:mb-0">
            <img src={logo} alt="CoroApp Logo" className="w-12 h-12 sm:w-16 sm:h-16 mr-4" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-mono uppercase tracking-wider">Panel de Administración</h1>
              <p className="text-xs sm:text-sm font-mono">
                Siguiente versión: {currentVersion} | Versión en Producción: {productionVersion}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-4 justify-center sm:justify-end">
            <button
              onClick={() => {
                setTestModeAction(isTestMode ? 'deactivate' : 'activate');
                setIsTestModeModalOpen(true);
              }}
              className={`px-3 py-2 sm:px-4 sm:py-2 border transition-colors font-mono text-xs uppercase tracking-wider ${
                isTestMode
                  ? 'border-red-500 text-red-500 bg-white hover:bg-red-500 hover:text-white'
                  : 'border-gray-600 text-gray-800 bg-white hover:bg-gray-600 hover:text-white'
              }`}
              disabled={loading}
            >
              {isTestMode ? 'Desactivar Modo Prueba' : 'Activar Modo Prueba'}
            </button>
            <button
              onClick={() => setIsProductionModalOpen(true)}
              className="px-3 py-2 sm:px-4 sm:py-2 border border-gray-600 text-gray-800 bg-white hover:bg-gray-600 hover:text-white transition-colors font-mono text-xs uppercase tracking-wider disabled:opacity-50"
              disabled={loading || !hasPendingChanges()}
            >
              {loading ? 'Enviando...' : 'Enviar a Producción'}
            </button>
            <button
              onClick={handleSignOut}
              className="px-3 py-2 sm:px-4 sm:py-2 border border-gray-600 text-gray-800 bg-white hover:bg-gray-600 hover:text-white transition-colors font-mono text-xs uppercase tracking-wider"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 border border-gray-600 bg-gray-800 text-white font-mono text-sm">
            {error}
          </div>
        )}
        {/* Test Mode Banner */}
        {isTestMode && (
          <div className="mb-6 p-4 border border-red-500 bg-red-100 text-red-700 font-mono text-sm text-center">
            <strong>MODO PRUEBA ACTIVADO.</strong> Los cambios no son permanentes y serán revertidos al desactivar.
          </div>
        )}
        {/* Recent Changes */}
        {hasPendingChanges() && (
          <div className="mb-8 p-4 border border-gray-600">
            <button
              className="w-full flex justify-between items-center bg-gray-200 p-3 font-mono text-sm uppercase tracking-wider"
              onClick={() => toggleVersion(`pending-${currentVersion}`)}
            >
              <span>Cambios en Versión {currentVersion} (Pendientes por enviar)</span>
              <span>{expandedVersions[`pending-${currentVersion}`] ? '−' : '+'}</span>
            </button>
            {expandedVersions[`pending-${currentVersion}`] && (
              <div className="p-4">
                {Object.entries(changes).flatMap(([type, items]) =>
                  items.map((item) => {
                    const filename = type === 'updated' && typeof item === 'object' ? item.filename : item;
                    const update = Object.values(updateHistoryByVersion[currentVersion] || {}).find(
                      (u) => u.filename === filename && u.type === type
                    );
                    console.log(`Checking ${type} filename: ${filename}, found update:`, update); // Debug log
                    if (!update) return null;
                    return (
                      <li key={`${type}-${filename}`} className="border-b border-gray-200 py-2 font-mono text-sm">
                        <p className="font-semibold mb-1">
                          {update.type === 'added' && `Agregada: ${update.name}`}
                          {update.type === 'updated' && (
                            <>
                              Actualizada: {update.name || update.filename}
                              {(update.pdfUpdated || update.oldName || update.oldThemes) && (
                                <>
                                  {' ('}
                                  {[
                                    update.pdfUpdated && 'Archivo PDF',
                                    update.oldName && 'Nombre',
                                    update.oldThemes && 'Temas',
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                  {')'}
                                </>
                              )}
                            </>
                          )}
                          {update.type === 'deleted' && `Eliminada: ${update.name}`}
                        </p>
                        <p className="text-xs opacity-70">Archivo: {update.filename}</p>
                        <p className="text-xs opacity-70">Usuario: {update.user}</p>
                        <p className="text-xs opacity-70">Fecha: {new Date(update.timestamp).toLocaleString()}</p>
                        <p className="text-xs opacity-70">Número de Actualización: {update.updateNumber}</p>
                        {update.type === 'added' && (
                          <p className="text-xs opacity-70">Temas: {update.themes?.join(', ') || 'N/A'}</p>
                        )}
                        {update.type === 'updated' && (
                          <>
                            {update.oldName && (
                              <p className="text-xs opacity-70">Nombre Anterior: {update.oldName}</p>
                            )}
                            {update.newName && (
                              <p className="text-xs opacity-70">Nombre Nuevo: {update.newName}</p>
                            )}
                            {update.oldThemes && (
                              <p className="text-xs opacity-70">Temas Anteriores: {update.oldThemes.join(', ')}</p>
                            )}
                            {update.newThemes && (
                              <p className="text-xs opacity-70">Temas Nuevos: {update.newThemes.join(', ')}</p>
                            )}
                            {update.pdfUpdated && (
                              <p className="text-xs opacity-70">Archivo PDF: Actualizado</p>
                            )}
                          </>
                        )}
                        {update.type === 'deleted' && (
                          <p className="text-xs opacity-70">Temas: {update.themes?.join(', ') || 'N/A'}</p>
                        )}
                      </li>
                    );
                  })
                ).filter(Boolean).length > 0 ? (
                  <ul className="space-y-4">
                    {Object.entries(changes).flatMap(([type, items]) =>
                      items.map((item) => {
                        const filename = type === 'updated' && typeof item === 'object' ? item.filename : item;
                        const update = Object.values(updateHistoryByVersion[currentVersion] || {}).find(
                          (u) => u.filename === filename && u.type === type
                        );
                        if (!update) return null;
                        return (
                          <li key={`${type}-${filename}`} className="border-b border-gray-200 py-2 font-mono text-sm">
                            <p className="font-semibold mb-1">
                              {update.type === 'added' && `Agregada: ${update.name}`}
                              {update.type === 'updated' && (
                                <>
                                  Actualizada: {update.name || update.filename}
                                  {(update.pdfUpdated || update.oldName || update.oldThemes) && (
                                    <>
                                      {' ('}
                                      {[
                                        update.pdfUpdated && 'Archivo PDF',
                                        update.oldName && 'Nombre',
                                        update.oldThemes && 'Temas',
                                      ]
                                        .filter(Boolean)
                                        .join(', ')}
                                      {')'}
                                    </>
                                  )}
                                </>
                              )}
                              {update.type === 'deleted' && `Eliminada: ${update.name}`}
                            </p>
                            <p className="text-xs opacity-70">Archivo: {update.filename}</p>
                            <p className="text-xs opacity-70">Usuario: {update.user}</p>
                            <p className="text-xs opacity-70">Fecha: {new Date(update.timestamp).toLocaleString()}</p>
                            <p className="text-xs opacity-70">Número de Actualización: {update.updateNumber}</p>
                            {update.type === 'added' && (
                              <p className="text-xs opacity-70">Temas: {update.themes?.join(', ') || 'N/A'}</p>
                            )}
                            {update.type === 'updated' && (
                              <>
                                {update.oldName && (
                                  <p className="text-xs opacity-70">Nombre Anterior: {update.oldName}</p>
                                )}
                                {update.newName && (
                                  <p className="text-xs opacity-70">Nombre Nuevo: {update.newName}</p>
                                )}
                                {update.oldThemes && (
                                  <p className="text-xs opacity-70">Temas Anteriores: {update.oldThemes.join(', ')}</p>
                                )}
                                {update.newThemes && (
                                  <p className="text-xs opacity-70">Temas Nuevos: {update.newThemes.join(', ')}</p>
                                )}
                                {update.pdfUpdated && (
                                  <p className="text-xs opacity-70">Archivo PDF: Actualizado</p>
                                )}
                              </>
                            )}
                            {update.type === 'deleted' && (
                              <p className="text-xs opacity-70">Temas: {update.themes?.join(', ') || 'N/A'}</p>
                            )}
                          </li>
                        );
                      })
                    ).filter(Boolean)}
                  </ul>
                ) : (
                  <p className="font-mono text-sm">No se encontraron detalles para los cambios pendientes.</p>
                )}
              </div>
            )}
          </div>
        )}
        {/* Add New Sheet Music Button */}
        <div className="mb-8">
          <button
            onClick={() => {
              setEditingSong(null);
              setNewSong({ name: '', filename: '', themes: [''] });
              setFile(null);
              setModalError('');
              setIsAddModalOpen(true);
            }}
            className="px-6 py-3 border border-gray-600 text-gray-800 bg-white hover:bg-gray-600 hover:text-white transition-colors font-mono text-sm uppercase tracking-wider"
          >
            + Agregar Nueva Partitura
          </button>
        </div>
        {/* Panels Container */}
        <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-300px)] min-h-[500px]">
          {/* Sheet Music List Panel */}
          <div className="flex-1 border border-gray-600 h-full overflow-hidden">
            <div className="bg-gray-800 text-white p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
                <h2 className="font-mono uppercase tracking-wider">Lista de Partituras</h2>
                <button
                  onClick={handleDownloadAll}
                  className="px-4 py-2 border border-gray-300 text-white bg-gray-900 hover:bg-gray-700 hover:text-gray-100 transition-colors font-mono text-xs uppercase tracking-wider disabled:opacity-50 w-full sm:w-auto"
                  disabled={loading || songs.length === 0}
                >
                  {loading ? 'Descargando...' : 'Descargar Todas (ZIP)'}
                </button>
              </div>
              <input
                type="text"
                placeholder="Buscar por nombre o tema..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2 bg-white text-gray-800 border-none font-mono text-sm placeholder-gray-500 focus:outline-none"
              />
            </div>
            <div className="h-[calc(100%-80px)] overflow-y-auto bg-white">
              {filteredSongs.length === 0 ? (
                <p className="p-4 font-mono text-sm">No se encontraron partituras que coincidan con la búsqueda.</p>
              ) : (
                <ul>
                  {filteredSongs.map((song, index) => (
                    <li
                      key={song.id}
                      className={`p-4 cursor-pointer transition-colors font-mono text-sm border-b border-gray-200 hover:bg-gray-200 ${selectedSong && selectedSong.id === song.id ? 'bg-gray-600 text-white' : ''}`}
                      onClick={() => handleSelectSong(song)}
                    >
                      <p className="font-semibold mb-1">{song.name}</p>
                      <p className="text-xs opacity-70">Archivo: {song.filename}</p>
                      <p className="text-xs opacity-70">Temas: {song.themes.join(', ')}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          {/* Historical Updates Panel */}
          <div className="flex-1 border border-gray-600 h-full overflow-hidden">
            <div className="bg-gray-800 text-white p-4">
              <h2 className="font-mono uppercase tracking-wider mb-4">Historial de Actualizaciones</h2>
            </div>
            <div className="h-[calc(100%-80px)] overflow-y-auto bg-white">
              {Object.keys(updateHistoryByVersion).filter(version => Number(version) <= productionVersion).length === 0 ? (
                <p className="p-4 font-mono text-sm">No hay actualizaciones enviadas a producción.</p>
              ) : (
                <div className="space-y-4 p-4">
                  {Object.keys(updateHistoryByVersion)
                    .filter(version => Number(version) <= productionVersion)
                    .sort((a, b) => Number(b) - Number(a))
                    .map((version) => (
                      <div key={version} className="border border-gray-600 rounded">
                        <button
                          className="w-full flex justify-between items-center bg-gray-200 p-3 font-mono text-sm uppercase tracking-wider"
                          onClick={() => toggleVersion(version)}
                        >
                          <span>Versión {version}</span>
                          <span>{expandedVersions[version] ? '−' : '+'}</span>
                        </button>
                        {expandedVersions[version] && (
                          <ul className="p-4">
                            {updateHistoryByVersion[version].map((update) => (
                              <li key={update.id} className="border-b border-gray-200 py-2 font-mono text-sm">
                                <p className="font-semibold mb-1">
                                  {update.type === 'added' && `Agregada: ${update.name}`}
                                  {update.type === 'updated' && (
                                    <>
                                      Actualizada: {update.name || update.filename}
                                      {(update.pdfUpdated || update.oldName || update.oldThemes) && (
                                        <>
                                          {' ('}
                                          {[
                                            update.pdfUpdated && 'Archivo PDF',
                                            update.oldName && 'Nombre',
                                            update.oldThemes && 'Temas',
                                          ]
                                            .filter(Boolean)
                                            .join(', ')}
                                          {')'}
                                        </>
                                      )}
                                    </>
                                  )}
                                  {update.type === 'deleted' && `Eliminada: ${update.name}`}
                                </p>
                                <p className="text-xs opacity-70">Archivo: {update.filename}</p>
                                <p className="text-xs opacity-70">Usuario: {update.user}</p>
                                <p className="text-xs opacity-70">Fecha: {new Date(update.timestamp).toLocaleString()}</p>
                                <p className="text-xs opacity-70">Número de Actualización: {update.updateNumber}</p>
                                {update.type === 'added' && (
                                  <p className="text-xs opacity-70">Temas: {update.themes?.join(', ') || 'N/A'}</p>
                                )}
                                {update.type === 'updated' && (
                                  <>
                                    {update.oldName && (
                                      <p className="text-xs opacity-70">Nombre Anterior: {update.oldName}</p>
                                    )}
                                    {update.newName && (
                                      <p className="text-xs opacity-70">Nombre Nuevo: {update.newName}</p>
                                    )}
                                    {update.oldThemes && (
                                      <p className="text-xs opacity-70">Temas Anteriores: {update.oldThemes.join(', ')}</p>
                                    )}
                                    {update.newThemes && (
                                      <p className="text-xs opacity-70">Temas Nuevos: {update.newThemes.join(', ')}</p>
                                    )}
                                    {update.pdfUpdated && (
                                      <p className="text-xs opacity-70">Archivo PDF: Actualizado</p>
                                    )}
                                  </>
                                )}
                                {update.type === 'deleted' && (
                                  <p className="text-xs opacity-70">Temas: {update.themes?.join(', ') || 'N/A'}</p>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Modal for Adding/Editing Sheet Music */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-2 border-gray-600 max-w-md w-full max-h-[80vh] overflow-y-auto m-4">
            <div className="bg-gray-800 text-white p-4">
              <h2 className="font-mono uppercase tracking-wider">
                {editingSong ? 'Editar Partitura' : 'Agregar Nueva Partitura'}
              </h2>
            </div>
            <div className="p-6">
              {modalError && (
                <div className="mb-6 p-4 border border-gray-600 bg-red-800 text-white font-mono text-sm">
                  {modalError}
                </div>
              )}
              <div onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block font-mono text-xs uppercase tracking-wider mb-2">Nombre de la Partitura</label>
                  <input
                    type="text"
                    name="name"
                    value={newSong.name}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-600 font-mono text-sm focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block font-mono text-xs uppercase tracking-wider mb-2">Nombre del Archivo (Auto-generado)</label>
                  <input
                    type="text"
                    name="filename"
                    value={newSong.filename}
                    className="w-full p-3 border border-gray-600 bg-gray-100 font-mono text-sm"
                    disabled
                  />
                </div>
                <div>
                  <label className="block font-mono text-xs uppercase tracking-wider mb-2">Temas</label>
                  {newSong.themes.map((theme, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <select
                        name="themes"
                        value={theme}
                        onChange={(e) => handleInputChange(e, index)}
                        className="flex-1 p-3 border border-gray-600 font-mono text-sm focus:outline-none"
                        required
                      >
                        <option value="">Seleccionar tema</option>
                        {availableThemes.length > 0 ? (
                          availableThemes.map((availableTheme) => (
                            <option key={availableTheme} value={availableTheme}>
                              {availableTheme}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>
                            No hay temas disponibles
                          </option>
                        )}
                      </select>
                      {newSong.themes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTheme(index)}
                          className="px-3 py-1 border border-gray-600 hover:bg-gray-600 hover:text-white transition-colors font-mono text-xs"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addTheme}
                    className="px-4 py-2 border border-gray-600 hover:bg-gray-600 hover:text-white transition-colors font-mono text-xs uppercase tracking-wider"
                  >
                    + Agregar Tema
                  </button>
                </div>
                <div>
                  <label className="block font-mono text-xs uppercase tracking-wider mb-2">Archivo PDF</label>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    className="w-full p-3 border border-gray-600 font-mono text-sm file:border-0 file:bg-gray-600 file:text-white file:font-mono file:text-xs file:uppercase file:tracking-wider file:px-4 file:py-2 file:mr-4"
                    required={!editingSong}
                  />
                </div>
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="px-6 py-3 border border-gray-600 bg-gray-600 text-white hover:bg-white hover:text-gray-800 transition-colors font-mono text-xs uppercase tracking-wider disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Guardando...' : editingSong ? 'Actualizar' : 'Agregar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddModalOpen(false);
                      setEditingSong(null);
                      setNewSong({ name: '', filename: '', themes: [''] });
                      setFile(null);
                      setModalError('');
                    }}
                    className="px-6 py-3 border border-gray-600 hover:bg-gray-600 hover:text-white transition-colors font-mono text-xs uppercase tracking-wider"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal for PDF Preview */}
      {isPreviewModalOpen && selectedSong && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-2 border-gray-600 max-w-5xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="bg-gray-800 text-white p-4 flex justify-between items-center">
              <h2 className="font-mono uppercase tracking-wider">Vista Previa del PDF</h2>
              <button
                onClick={() => {
                  setIsPreviewModalOpen(false);
                  setSelectedSong(null);
                  setPreviewUrl('');
                  setOriginalSize(null);
                  setCompressedSize(null);
                }}
                className="text-white hover:text-gray-300 text-xl font-mono"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6 pb-4 border-b border-gray-600">
                <p className="font-mono font-semibold text-lg mb-2">{selectedSong.name}</p>
                <p className="font-mono text-sm mb-1">Archivo: {selectedSong.filename}</p>
                <p className="font-mono text-sm mb-4">Temas: {selectedSong.themes.join(', ')}</p>
                {originalSize && compressedSize && (
                  <div className="font-mono text-sm mb-4">
                    <p>Peso Original: {formatBytes(originalSize)}</p>
                    <p>Peso Comprimido: {formatBytes(compressedSize)}</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => {
                      handleEdit(selectedSong);
                      setIsPreviewModalOpen(false);
                    }}
                    className="px-4 py-2 border border-gray-600 hover:bg-gray-600 hover:text-white transition-colors font-mono text-xs uppercase tracking-wider"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(selectedSong)}
                    className="px-4 py-2 border border-gray-600 hover:bg-gray-600 hover:text-white transition-colors font-mono text-xs uppercase tracking-wider"
                    disabled={loading}
                  >
                    {loading ? 'Eliminando...' : 'Eliminar'}
                  </button>
                </div>
              </div>
              {previewUrl ? (
                loading ? (
                  <div className="flex justify-center items-center h-[60vh]">
                    <div className="w-8 h-8 border-2 border-gray-600 border-t-transparent animate-spin"></div>
                  </div>
                ) : (
                  <iframe
                    src={`${previewUrl}#toolbar=0`}
                    className="w-full h-[60vh] border border-gray-600"
                    title="Vista Previa del PDF"
                    onError={handlePdfError}
                  ></iframe>
                )
              ) : (
                <p className="font-mono text-sm text-center py-12">No hay PDF disponible para esta partitura.</p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Modal for Delete Confirmation */}
      {isDeleteModalOpen && songToDelete && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-2 border-gray-600 max-w-md w-full m-4">
            <div className="bg-gray-800 text-white p-4">
              <h2 className="font-mono uppercase tracking-wider">Confirmar Eliminación</h2>
            </div>
            <div className="p-6">
              <p className="font-mono text-sm mb-6">
                ¿Estás seguro de que deseas eliminar la partitura "{songToDelete.name}"? Esta acción no se puede deshacer.
              </p>
              <div className="mb-4 p-3 border border-yellow-600 bg-yellow-100 text-yellow-800 font-mono text-sm rounded">
                <strong>Nota importante:</strong> Si el canto va a ser eliminado definitivamente, proceda a eliminar. De lo contrario, use la opción que permite actualizar el archivo.
              </div>
              <p className="font-mono text-sm mb-4 text-gray-600">
                Escribe "Eliminar partitura" para confirmar:
              </p>
              <input
                type="text"
                value={deleteConfirmationText}
                onChange={(e) => setDeleteConfirmationText(e.target.value)}
                placeholder="Eliminar partitura"
                className="w-full p-3 border border-gray-600 font-mono text-sm focus:outline-none mb-6"
              />
              <div className="flex gap-4">
                <button
                  onClick={confirmDelete}
                  className="px-6 py-3 border border-gray-600 bg-gray-600 text-white hover:bg-white hover:text-gray-800 transition-colors font-mono text-xs uppercase tracking-wider disabled:opacity-50"
                  disabled={loading || deleteConfirmationText.trim() !== 'Eliminar partitura'}
                >
                  {loading ? 'Eliminando...' : 'Confirmar'}
                </button>
                <button
                  onClick={cancelDelete}
                  className="px-6 py-3 border border-gray-600 hover:bg-gray-600 hover:text-white transition-colors font-mono text-xs uppercase tracking-wider"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isDownloadModalOpen && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-2 border-gray-600 max-w-md w-full m-4">
            <div className="bg-gray-800 text-white p-4">
              <h2 className="font-mono uppercase tracking-wider">Confirmar Descarga</h2>
            </div>
            <div className="p-6">
              <p className="font-mono text-sm mb-6">
                ¿Estás seguro de que deseas descargar todas las partituras? Esto puede generar una alta demanda de recursos, ya que se descargarán múltiples archivos.
              </p>
              <div className="mb-4 p-3 border border-yellow-600 bg-yellow-100 text-yellow-800 font-mono text-sm rounded">
                <strong>Nota importante:</strong> Proceda solo si es estrictamente necesario.
              </div>
              <div className="flex gap-4">
                <button
                  onClick={confirmDownloadAll}
                  className="px-6 py-3 border border-gray-600 bg-gray-600 text-white hover:bg-white hover:text-gray-800 transition-colors font-mono text-xs uppercase tracking-wider disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Descargando...' : 'Confirmar'}
                </button>
                <button
                  onClick={cancelDownload}
                  className="px-6 py-3 border border-gray-600 hover:bg-gray-600 hover:text-white transition-colors font-mono text-xs uppercase tracking-wider"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal for Production Confirmation */}
      {isProductionModalOpen && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-2 border-gray-600 max-w-md w-full m-4">
            <div className="bg-gray-800 text-white p-4">
              <h2 className="font-mono uppercase tracking-wider">Confirmar Envío a Producción</h2>
            </div>
            <div className="p-6">
              <p className="font-mono text-sm mb-6">
                ¿Estás listo para enviar los cambios de la versión {currentVersion} a producción? Esta acción no se puede deshacer.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={handleSendToProduction}
                  className="px-6 py-3 border border-gray-600 bg-gray-600 text-white hover:bg-white hover:text-gray-800 transition-colors font-mono text-xs uppercase tracking-wider disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Enviando...' : 'Confirmar'}
                </button>
                <button
                  onClick={() => setIsProductionModalOpen(false)}
                  className="px-6 py-3 border border-gray-600 hover:bg-gray-600 hover:text-white transition-colors font-mono text-xs uppercase tracking-wider"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal for Test Mode Confirmation */}
{isTestModeModalOpen && (
  <div className="fixed inset-0 bg-gray-900 bg-opacity-90 flex items-center justify-center z-50 p-4">
    <div className="bg-white border-2 border-gray-600 max-w-md w-full m-4">
      <div className="bg-gray-800 text-white p-4">
        <h2 className="font-mono uppercase tracking-wider">
          {testModeAction === 'activate' ? 'Activar Modo Prueba' : 'Desactivar Modo Prueba'}
        </h2>
      </div>
      <div className="p-6">
        <p className="font-mono text-sm mb-6">
          {testModeAction === 'activate'
            ? 'Al activar el modo prueba, todos los cambios (agregados, ediciones, eliminaciones) se registrarán temporalmente. Podrás revertirlos al desactivar el modo. Ingresa la contraseña para continuar. Nota: en la app Android, asigna a productionVersion, el valor: -110'
            : 'Al desactivar el modo prueba, todos los cambios realizados serán revertidos a su estado original. Esta acción no se puede deshacer. ¿Estás seguro?'}
        </p>
        {modalError && (
          <div className="mb-6 p-4 border border-gray-600 bg-red-800 text-white font-mono text-sm">
            {modalError}
          </div>
        )}
        {testModeAction === 'activate' && (
          <div className="mb-6">
            <label className="block font-mono text-xs uppercase tracking-wider mb-2">Contraseña</label>
            <input
              type="password"
              value={testModePassword}
              onChange={(e) => setTestModePassword(e.target.value)}
              className="w-full p-3 border border-gray-600 font-mono text-sm focus:outline-none"
              placeholder="Ingresa la contraseña"
            />
          </div>
        )}
        <div className="flex gap-4">
          <button
            onClick={handleToggleTestMode}
            className="px-6 py-3 border border-gray-600 bg-gray-600 text-white hover:bg-white hover:text-gray-800 transition-colors font-mono text-xs uppercase tracking-wider disabled:opacity-50"
            disabled={loading || (testModeAction === 'activate' && !testModePassword)}
          >
            {loading ? 'Procesando...' : 'Confirmar'}
          </button>
          <button
            onClick={() => {
              setIsTestModeModalOpen(false);
              setTestModePassword(''); // Limpiar la contraseña al cancelar
              setModalError('');
            }}
            className="px-6 py-3 border border-gray-600 hover:bg-gray-600 hover:text-white transition-colors font-mono text-xs uppercase tracking-wider"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default AdminPanel;